"use client"

import styled from "@emotion/styled"
import { Box, IconButton, Link as MuiLink, Typography } from "@mui/material"
import Image from "next/image"
import { useRouter } from "next/navigation"
import { useCallback, useEffect, useRef, useState } from "react"

import { iconsMap } from "@/public/icons/iconsMap"
import { sendBlockEvent } from "@/utils/gtm"
import { useIsDesktop } from "ui"
import { colors, sizes } from "ui/theme/theme"
import {
  extractMenuProperties,
  getNonUmbracoUrl,
  getUmbracoMediaUrl,
} from "utils"

interface StickyActionsProps {
  data: any
  locale: string
  id?: string
}

const StickyContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isOpen",
})<{ isOpen: boolean }>`
  position: fixed;
  top: 180px;
  inset-inline-end: 0;
  z-index: 1050;
  background-color: ${colors.white};
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  flex-direction: column;
  transition: all 0.3s ease-in-out;
  border-start-start-radius: ${sizes.borderRadius || "8px"};
  border-start-end-radius: 0;
  border-end-start-radius: ${sizes.borderRadius || "8px"};
  border-end-end-radius: 0;
  display: flex;

  width: 80px;
  padding: ${sizes.baseHalf};
  align-items: center;

  /* Accessibility: Focus-visible styles */
  &:focus-visible {
    outline: 2px solid ${colors.blue || "#4AA1B2"};
    outline-offset: 2px;
  }
`

const Header = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  width: 100%;
  gap: ${sizes.baseHalf};
  padding: 12px;
  border-radius: ${sizes.borderRadius || "8px"};
  transition: background-color 0.2s ease-in-out;

  &:hover {
    background-color: ${colors.lightGray || "#f5f5f5"};
  }

  &:focus-visible {
    outline: 2px solid ${colors.blue || "#4AA1B2"};
    outline-offset: 2px;
    background-color: ${colors.lightGray || "#f5f5f5"};
  }

  svg {
    margin: -8px 0 -10px 0;
  }
`

const TitleText = styled(Typography)`
  font-weight: bold;
  color: ${colors.darkBlue || colors.blue?.[500]};
  text-align: center;
  line-height: 1.2;
  font-size: 16px;

  /* Always horizontal, break into 2 lines */
  writing-mode: horizontal-tb;
  max-width: 70px;
  word-wrap: break-word;
  white-space: normal;
  overflow-wrap: break-word;
  hyphens: auto;
`

const ActionsList = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isOpen",
})<{ isOpen: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${sizes.baseHalf};
  overflow: hidden;
  max-height: ${({ isOpen }) => (isOpen ? "calc(70vh - 100px)" : "0")};
  opacity: ${({ isOpen }) => (isOpen ? "1" : "0")};
  transform: ${({ isOpen }) =>
    isOpen ? "translateY(0)" : "translateY(-30px)"};
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  margin-top: ${({ isOpen }) => (isOpen ? sizes.baseSingle : "0")};
  width: 100%;

  /* Slide from top effect */
  transform-origin: top;
  visibility: ${({ isOpen }) => (isOpen ? "visible" : "hidden")};
`

const ActionItem = styled(MuiLink)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${sizes.baseHalf};
  padding: ${sizes.baseHalf};
  border-radius: ${sizes.borderRadius || "8px"};
  text-decoration: none;
  color: ${colors.black};
  transition: all 0.2s ease-in-out;
  min-height: 60px;
  width: 100%;

  &:hover {
    background-color: ${colors.lightGray || "#f5f5f5"};
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    cursor: pointer;
  }

  &:focus-visible {
    outline: 2px solid ${colors.blue || "#4AA1B2"};
    outline-offset: 2px;
    background-color: ${colors.lightGray || "#f5f5f5"};
  }

  &:active {
    transform: translateY(0);
  }
`

const ActionIconContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "bgcolor",
})<{ bgcolor?: string }>`
  position: relative;
  width: 42px;
  height: 42px;
  border-radius: ${sizes.baseSingle || "8px"};
  background-color: ${({ bgcolor }) => bgcolor || colors.blue};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);

  img {
    filter: brightness(0) invert(1); // Make icons white
  }
`

const ActionText = styled(Typography)`
  font-size: 10px;
  font-weight: 400;
  line-height: 1.2;
  text-align: center;
  max-width: 100%;
  word-wrap: break-word;
  overflow-wrap: break-word;
  color: ${colors.black};
`

const StickyActions = ({ data, id }: StickyActionsProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const isDesktop = useIsDesktop()
  const router = useRouter()
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const items = data?.servicesList?.blocks || []

  // Extract title using the same pattern as Services component
  const transformedData = data
  const titleText = transformedData?.title?.value || "פעולות נפוצות"

  // Performance optimization: Use useCallback for event handlers
  const handleMouseEnter = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
    setIsOpen(true)
  }, [])

  const handleMouseLeave = useCallback(() => {
    timeoutRef.current = setTimeout(() => {
      setIsOpen(false)
    }, 300) // 300ms delay before closing
  }, [])

  const handleClick = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
    setIsOpen(!isOpen)
  }, [isOpen])

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === "Enter") {
        handleClick()
      } else if (event.key === "Escape" && isOpen) {
        setIsOpen(false)
      }
    },
    [handleClick, isOpen],
  )

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  if (!isDesktop) {
    return null
  }

  const handleActionClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    url: string,
    title: string,
  ) => {
    if (url && url !== "#") {
      e.preventDefault()

      // GTM tracking
      sendBlockEvent({
        blockId: id || "sticky-actions",
        componentName: "פעולות נפוצות",
        title: titleText,
        actionName: undefined,
        buttonName: title,
      })

      router.push(getNonUmbracoUrl(url))
    }
  }

  const Arrow = iconsMap.arrow_red_white

  return (
    <StickyContainer
      isOpen={isOpen}
      ref={containerRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      role="navigation"
      aria-label="פעולות נפוצות"
      aria-expanded={isOpen}
    >
      <Header
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        tabIndex={0}
        role="button"
        aria-label={`${titleText} - ${isOpen ? "סגור" : "פתח"} תפריט`}
        aria-controls="sticky-actions-list"
      >
        <TitleText variant="h6" id="sticky-actions-title">
          {titleText}
        </TitleText>
        <IconButton
          size="small"
          aria-label={isOpen ? "סגירת פעולות" : "פתיחת פעולות"}
          tabIndex={-1} // Remove from tab order since header handles keyboard interaction
          sx={{
            color: colors.blue || colors.blue?.[500],
            "& .arrow-icon": {
              transform: !isOpen
                ? "scale(0.7) rotate(-90deg)"
                : "scale(0.7) rotate(90deg)",
              transition: "transform 0.3s ease-in-out",
            },
          }}
        >
          <Arrow className="arrow-icon" />
        </IconButton>
      </Header>
      <ActionsList
        isOpen={isOpen}
        id="sticky-actions-list"
        role="menu"
        aria-labelledby="sticky-actions-title"
        onKeyDown={handleKeyDown}
      >
        {items.map((item: any, index: number) => {
          // Extract properties using the same pattern as Services component
          const {
            url: link,
            logo,
            primaryColor,
            title: titleProp,
          } = extractMenuProperties(item)

          const title = titleProp || link?.name || "Link"
          const rawUrl = link?.url || "#"
          let iconSrc = logo || ""
          if (iconSrc && !iconSrc.startsWith("http")) {
            iconSrc = getUmbracoMediaUrl(iconSrc)
          }

          return (
            <ActionItem
              key={index}
              href={getNonUmbracoUrl(rawUrl)}
              onClick={(e: React.MouseEvent<HTMLAnchorElement>) =>
                handleActionClick(e, rawUrl, title)
              }
              role="menuitem"
              aria-label={`עבור אל ${title}`}
            >
              {iconSrc && (
                <ActionIconContainer bgcolor={`#${primaryColor || "4AA1B2"}`}>
                  <Image
                    src={iconSrc}
                    alt="" // Decorative image, title is already provided in aria-label
                    width={42}
                    height={42}
                    style={{ objectFit: "contain" }}
                  />
                </ActionIconContainer>
              )}
              <ActionText aria-hidden="true">{title}</ActionText>{" "}
              {/* Hidden from screen readers since info is in aria-label */}
            </ActionItem>
          )
        })}
      </ActionsList>
    </StickyContainer>
  )
}

export default StickyActions
